/**
 * Video reels row for sections/shugar-video-reels.liquid.
 *
 * Cards sit in a native scroll-snap track, so swiping and keyboard scrolling
 * work without JavaScript. This element adds the arrows, the looping, optional
 * auto-scroll, and the playback rules: a clip plays only while it is on screen,
 * and only one clip at a time is allowed to have sound.
 */
class ShugarVideoReels extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement[]} */
  #items = [];
  /** @type {HTMLElement[]} */
  #arrows = [];
  /** @type {IntersectionObserver | null} */
  #cards = null;
  /** @type {IntersectionObserver | null} */
  #visibility = null;
  /** @type {number | null} */
  #timer = null;
  /** Cards currently scrolled into view inside the track. @type {Set<Element>} */
  #inView = new Set();
  #paused = false;
  #offscreen = true;

  connectedCallback() {
    this.#track = this.querySelector('.shugar-reels__track');
    this.#items = [...this.querySelectorAll('.shugar-reels__item')];
    this.#arrows = [...this.querySelectorAll('[data-reel-step]')];
    if (!this.#track || this.#items.length === 0) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    this.addEventListener('touchstart', this.#pause, { passive: true });
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#resume);

    // A clip plays only while its own card is on screen, so a row of six does
    // not decode six videos at once.
    this.#cards = new IntersectionObserver(this.#onCardVisible, { root: this.#track, threshold: 0.55 });
    this.#items.forEach((item) => this.#cards?.observe(item));

    // Nothing plays at all while the whole row is below the fold.
    this.#visibility = new IntersectionObserver(this.#onSectionVisible, { threshold: 0 });
    this.#visibility.observe(this);

    this.#updateArrows();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.#cards?.disconnect();
    this.#visibility?.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
    this.removeEventListener('touchstart', this.#pause);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#resume);
  }

  /**
   * Scrolls by whole cards. At either end it wraps around when looping is on,
   * which is what makes the row feel endless rather than hitting a wall.
   * @param {number} step
   */
  move(step) {
    const track = this.#track;
    if (!track) return;

    const card = this.#items[0];
    const width = card ? card.getBoundingClientRect().width + this.#gap() : track.clientWidth;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduceMotion ? 'auto' : 'smooth';

    let target = track.scrollLeft + width * step;

    if (this.dataset.loop === 'true') {
      // A pixel of slack: scrollLeft is fractional on zoomed or scaled displays.
      if (target > maxScroll + 1) {
        target = 0;
      } else if (target < -1) {
        target = maxScroll;
      }
    }

    track.scrollTo({ left: Math.max(0, Math.min(target, maxScroll)), behavior });
  }

  #gap() {
    if (!this.#track) return 0;
    const gap = window.getComputedStyle(this.#track).columnGap;
    return Number.parseFloat(gap) || 0;
  }

  /** @returns {HTMLVideoElement[]} */
  #videos() {
    return [...this.querySelectorAll('video')];
  }

  /**
   * A clip plays only when its card is scrolled into view inside the track AND
   * the row itself is on screen. Both observers funnel through here, because
   * scrolling the page vertically never changes intersection inside the track —
   * so the card observer alone would leave the clips paused forever.
   */
  #applyPlayback() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    for (const item of this.#items) {
      const video = item.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) continue;

      const shouldPlay = !this.#offscreen && this.#inView.has(item);

      if (!shouldPlay) {
        if (!video.paused) video.pause();
        continue;
      }

      if (reduceMotion || !video.paused) continue;
      // Muted is what makes autoplay allowed; Safari also wants it set in JS.
      if (video.dataset.unmuted !== 'true') video.muted = true;
      video.play().catch(() => {
        // Autoplay refused — the cover image stays up, which is a fine fallback.
      });
    }
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onCardVisible = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.#inView.add(entry.target);
      } else {
        this.#inView.delete(entry.target);
      }
    }

    this.#applyPlayback();
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onSectionVisible = (entries) => {
    for (const entry of entries) {
      this.#offscreen = !entry.isIntersecting;
    }

    this.#applyPlayback();
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target) return;

    const arrow = target.closest('[data-reel-step]');
    if (arrow instanceof HTMLElement) {
      this.#pause();
      this.move(Number(arrow.dataset.reelStep));
      this.#resume();
      return;
    }

    const toggle = target.closest('[data-sound-toggle]');
    if (toggle instanceof HTMLElement) {
      event.preventDefault();
      this.#toggleSound(toggle);
    }
  };

  /**
   * Sound is exclusive: turning one clip up mutes every other one, so a row of
   * cards can never talk over itself.
   * @param {HTMLElement} toggle
   */
  #toggleSound(toggle) {
    const card = toggle.closest('.shugar-reels__card');
    const video = card?.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    const turningOn = video.muted;

    for (const other of this.#videos()) {
      other.muted = true;
      delete other.dataset.unmuted;
    }
    for (const button of this.querySelectorAll('[data-sound-toggle]')) {
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', 'Turn sound on');
    }

    if (turningOn) {
      video.muted = false;
      video.dataset.unmuted = 'true';
      toggle.setAttribute('aria-pressed', 'true');
      toggle.setAttribute('aria-label', 'Turn sound off');
      video.play().catch(() => {
        // Refused with sound on — fall back to muted playback.
        video.muted = true;
        delete video.dataset.unmuted;
        toggle.setAttribute('aria-pressed', 'false');
        toggle.setAttribute('aria-label', 'Turn sound on');
      });
    }
  }

  #onScroll = () => {
    this.#updateArrows();
  };

  /** With looping off, an arrow that cannot go anywhere is hidden rather than dead. */
  #updateArrows() {
    if (!this.#track || this.#arrows.length === 0) return;
    if (this.dataset.loop === 'true') return;

    const maxScroll = this.#track.scrollWidth - this.#track.clientWidth;
    for (const arrow of this.#arrows) {
      const step = Number(arrow.dataset.reelStep);
      const atStart = this.#track.scrollLeft <= 1;
      const atEnd = this.#track.scrollLeft >= maxScroll - 1;
      arrow.toggleAttribute('hidden', step < 0 ? atStart : atEnd);
    }
  }

  /**
   * In the theme editor, selecting a clip scrolls to it and holds it there.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const block = /** @type {HTMLElement | null} */ (event.target);
    const item = this.#items.find((candidate) => candidate === block || candidate.contains(block));
    if (!item || !this.#track) return;

    this.#pause();
    this.#track.scrollTo({ left: item.offsetLeft - this.#track.offsetLeft, behavior: 'smooth' });
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#stop();
      for (const video of this.#videos()) video.pause();
    } else if (!this.#paused) {
      this.#applyPlayback();
      this.#start();
    }
  };

  #pause = () => {
    this.#paused = true;
    this.#stop();
  };

  #resume = () => {
    this.#paused = false;
    this.#start();
  };

  #start() {
    this.#stop();
    if (this.dataset.autoplay !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const interval = Math.max(2000, Number(this.dataset.interval) || 5000);
    this.#timer = window.setInterval(() => this.move(1), interval);
  }

  #stop() {
    if (this.#timer !== null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }
}

if (!customElements.get('shugar-video-reels')) {
  customElements.define('shugar-video-reels', ShugarVideoReels);
}
